import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTheme } from '@/contexts/ThemeContext';
import { loadStaticData } from '@/lib/offlineData';
import { type StaticHospitalData, type StaticMapping } from '@/hooks/useStaticHospitalData';
import {
  COMMON_CARE_CODES,
  COMMON_CARE_GROUPS,
  normalizeDrg,
  type CommonCareCode,
  type CommonCareFamily,
} from '@/lib/commonCareCodes';

/**
 * Consumer-facing summary that sits above a hospital's full price list.
 *
 *   1. Services listed          — how much of the file carries a list / cash price
 *   2. Cash / self-pay discount — the median published cash discount (what an
 *                                 uninsured patient should ask for)
 *   3. Common services above the U.S. median — a consumer-scale read of the
 *                                 hospital's pricing, using the same median
 *                                 baseline the national rank uses
 *
 * followed by a "What common care costs here" table: ER visits, imaging, lab
 * tests, everyday surgeries and common inpatient stays, each with the list
 * price, the cash price and where it lands against the U.S. median for that
 * exact billing code. Clicking a row filters the price list below to it.
 *
 * All hospital figures come from the per-hospital static JSON already loaded
 * for the page; the U.S. median / p10 / p90 come from codes_stats_<family>.json
 * on the CDN (the same files the /common-procedures lookup uses).
 */

interface CodeStatEntry { med: number; p10: number; p90: number; avg?: number; th?: number; n?: string }
interface CodeStatsFile { count: number; coverage?: { hospitals?: number }; data: Record<string, CodeStatEntry> }

type Position = 'lowest10' | 'below' | 'above' | 'highest10';
type Tone = 'good' | 'neutral' | 'bad' | 'na';

interface CommonRow {
  def: CommonCareCode;
  price: number;
  cash: number | null;
  /** The hospital's own line description for this code, as published. */
  desc?: string | null;
  median?: number;
  p10?: number;
  p90?: number;
  hospitals?: number;
  position?: Position;
  diffPct?: number;
}

interface HospitalPriceSummaryProps {
  staticData?: StaticHospitalData;
  /** Deduped service count (works for both static and Supabase-backed hospitals). */
  totalListed: number;
  /** True while the per-hospital static JSON is still downloading (it can be several MB). */
  loading?: boolean;
  onPickCode: (code: string, family: CommonCareFamily) => void;
}

const INITIAL_ROWS = 12;
const DAY_MS = 24 * 60 * 60 * 1000;

const fmtUsd = (v: number) => `$${Math.round(v).toLocaleString()}`;
const fmtPct = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Math.round(v))}%`;

function median(nums: number[]): number | null {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Among a code's rows pick the one a consumer would be billed: preferred setting, highest list price. */
function pickRow(rows: StaticMapping[], preferIo: 'outpatient' | 'inpatient'): StaticMapping | null {
  const priced = rows.filter(r => (r.gc ?? 0) > 0);
  if (!priced.length) return null;
  const preferred = priced.filter(r => (r.io || '').toLowerCase() === preferIo);
  const pool = preferred.length ? preferred : priced;
  return pool.reduce((best, r) => ((r.gc ?? 0) > (best.gc ?? 0) ? r : best));
}

function positionOf(price: number, s: CodeStatEntry): Position {
  if (price < s.p10) return 'lowest10';
  if (price <= s.med) return 'below';
  if (price <= s.p90) return 'above';
  return 'highest10';
}

const POSITION_TONE: Record<Position, Tone> = { lowest10: 'good', below: 'good', above: 'neutral', highest10: 'bad' };

export function HospitalPriceSummary({ staticData, totalListed, loading, onPickCode }: HospitalPriceSummaryProps) {
  const { t } = useTranslation(['hospital']);
  const { isDark } = useTheme();
  const [showAll, setShowAll] = useState(false);

  const mappings = staticData?.mappings;
  const hasMappings = !!(mappings && mappings.length > 0);
  const initialLoading = !!loading && !hasMappings;

  // ── File-level figures: list-price coverage, cash-price coverage, typical cash discount ──
  const fileStats = useMemo(() => {
    if (!hasMappings) return null;
    let priced = 0;
    let withCash = 0;
    const ratios: number[] = [];
    for (const m of mappings!) {
      const gc = m.gc ?? 0;
      if (gc <= 0) continue;
      priced++;
      const dc = m.dc ?? 0;
      if (dc > 0) {
        withCash++;
        const ratio = dc / gc;
        // Ignore obvious data junk (a $0.01 cash price, or a "cash price" 10x the list price).
        if (ratio > 0.005 && ratio <= 1.5) ratios.push(ratio);
      }
    }
    const medianRatio = median(ratios);
    return {
      priced,
      withCash,
      cashCoveragePct: priced ? Math.round((withCash / priced) * 100) : 0,
      discountPct: medianRatio == null ? null : Math.round((1 - medianRatio) * 100),
    };
  }, [hasMappings, mappings]);

  // ── Common-care rows present in this hospital's file ──
  const baseRows = useMemo<CommonRow[]>(() => {
    if (!hasMappings) return [];
    const byCpt = new Map<string, StaticMapping[]>();
    const byDrg = new Map<string, StaticMapping[]>();
    for (const m of mappings!) {
      if (m.cp) {
        const arr = byCpt.get(m.cp);
        if (arr) arr.push(m); else byCpt.set(m.cp, [m]);
      }
      if (m.dr) {
        const key = normalizeDrg(m.dr);
        if (key) {
          const arr = byDrg.get(key);
          if (arr) arr.push(m); else byDrg.set(key, [m]);
        }
      }
    }
    const rows: CommonRow[] = [];
    for (const def of COMMON_CARE_CODES) {
      const candidates = def.family === 'cpt' ? byCpt.get(def.code) : byDrg.get(def.code);
      if (!candidates?.length) continue;
      const row = pickRow(candidates, def.family === 'drg' ? 'inpatient' : 'outpatient');
      if (!row) continue;
      rows.push({ def, price: row.gc!, cash: (row.dc ?? 0) > 0 ? row.dc! : null, desc: row.pd || null });
    }
    return rows;
  }, [hasMappings, mappings]);

  const needCpt = baseRows.some(r => r.def.family === 'cpt');
  const needDrg = baseRows.some(r => r.def.family === 'drg');

  const { data: cptStats, isLoading: cptLoading } = useQuery({
    queryKey: ['codes-stats-file', 'cpt'],
    queryFn: () => loadStaticData<CodeStatsFile>('codes_stats_cpt.json', { cdnOnly: true }),
    staleTime: DAY_MS,
    gcTime: DAY_MS,
    enabled: needCpt,
  });
  const { data: drgStats, isLoading: drgLoading } = useQuery({
    queryKey: ['codes-stats-file', 'drg'],
    queryFn: () => loadStaticData<CodeStatsFile>('codes_stats_drg.json', { cdnOnly: true }),
    staleTime: DAY_MS,
    gcTime: DAY_MS,
    enabled: needDrg,
  });
  const statsLoading = (needCpt && cptLoading) || (needDrg && drgLoading);

  const rows = useMemo<CommonRow[]>(() => {
    return baseRows.map(r => {
      const file = r.def.family === 'cpt' ? cptStats : drgStats;
      const s = file?.data?.[r.def.code];
      if (!s || !(s.med > 0)) return r;
      return {
        ...r,
        median: s.med,
        p10: s.p10,
        p90: s.p90,
        hospitals: s.th,
        position: positionOf(r.price, s),
        diffPct: (r.price / s.med - 1) * 100,
      };
    });
  }, [baseRows, cptStats, drgStats]);

  const compared = rows.filter(r => r.position);
  const aboveCount = compared.filter(r => r.position === 'above' || r.position === 'highest10').length;
  const statsHospitals = cptStats?.coverage?.hospitals ?? drgStats?.coverage?.hospitals ?? null;
  const anyCash = rows.some(r => r.cash != null);
  const anyStats = compared.length > 0;

  // ── Tile 1: services listed ──
  const listedSub = initialLoading
    ? t('hospital:mappingsTable.summary.loadingSub')
    : !fileStats
    ? t('hospital:mappingsTable.summary.listedSubPlain')
    : fileStats.withCash > 0
      ? t('hospital:mappingsTable.summary.listedSub', {
          priced: fileStats.priced.toLocaleString(),
          cash: fileStats.withCash.toLocaleString(),
        })
      : t('hospital:mappingsTable.summary.listedSubNoCash', { priced: fileStats.priced.toLocaleString() });

  // ── Tile 2: cash / self-pay discount ──
  let cashTone: Tone = 'na';
  let cashValue = t('hospital:mappingsTable.summary.cashUnknown');
  let cashSub = t('hospital:mappingsTable.summary.cashUnknownSub');
  let cashPill: string | undefined;
  if (initialLoading) {
    cashSub = t('hospital:mappingsTable.summary.loadingSub');
  } else if (fileStats) {
    if (fileStats.withCash === 0 || fileStats.discountPct == null) {
      cashTone = 'bad';
      cashValue = t('hospital:mappingsTable.summary.cashNone');
      cashSub = t('hospital:mappingsTable.summary.cashNoneSub');
    } else if (fileStats.discountPct < 2) {
      cashTone = 'neutral';
      cashValue = t('hospital:mappingsTable.summary.cashNoDiscount');
      cashSub = t('hospital:mappingsTable.summary.cashNoDiscountSub', { pct: fileStats.cashCoveragePct });
    } else {
      cashTone = 'good';
      cashValue = t('hospital:mappingsTable.summary.cashValue', { pct: fileStats.discountPct });
      cashPill = t('hospital:mappingsTable.summary.cashPill');
      cashSub = t('hospital:mappingsTable.summary.cashSub', { pct: fileStats.cashCoveragePct });
    }
  }

  // ── Tile 3: common services above the U.S. median ──
  let aboveTone: Tone = 'na';
  let aboveValue = '—';
  let aboveSub: string;
  let abovePill: string | undefined;
  if (anyStats) {
    const share = aboveCount / compared.length;
    aboveTone = share <= 1 / 3 ? 'good' : share <= 2 / 3 ? 'neutral' : 'bad';
    abovePill = share <= 1 / 3
      ? t('hospital:mappingsTable.summary.pillGood')
      : share <= 2 / 3
        ? t('hospital:mappingsTable.summary.pillMixed')
        : t('hospital:mappingsTable.summary.pillBad');
    aboveValue = t('hospital:mappingsTable.summary.aboveValue', { above: aboveCount, total: compared.length });
    aboveSub = t('hospital:mappingsTable.summary.aboveSub');
  } else if (baseRows.length && statsLoading) {
    aboveSub = t('hospital:mappingsTable.summary.abovePending');
  } else if (baseRows.length) {
    aboveSub = t('hospital:mappingsTable.summary.aboveNoStats');
  } else if (initialLoading) {
    aboveSub = t('hospital:mappingsTable.summary.loadingSub');
  } else {
    aboveSub = t('hospital:mappingsTable.summary.aboveNone');
  }

  const visibleRows = showAll ? rows : rows.slice(0, INITIAL_ROWS);
  const hiddenCount = rows.length - visibleRows.length;

  // Group headers inside the table
  const grouped = COMMON_CARE_GROUPS
    .map(g => ({ group: g, rows: visibleRows.filter(r => r.def.group === g) }))
    .filter(g => g.rows.length > 0);

  const palette = isDark
    ? { good: '#10b981', neutral: 'var(--civic-gold)', bad: '#E0616F', na: '#7B8AA0', headBg: 'rgba(var(--civic-cream-rgb),0.04)', rowHover: 'rgba(var(--civic-cream-rgb),0.05)', chipBg: 'rgba(var(--civic-cream-rgb),0.08)', border: 'rgba(var(--civic-cream-rgb),0.10)' }
    : { good: '#0F8A5F', neutral: '#9A7B33', bad: 'var(--civic-red)', na: '#64748B', headBg: 'rgba(var(--civic-navy-deeper-rgb),0.03)', rowHover: 'rgba(var(--civic-navy-deeper-rgb),0.035)', chipBg: 'rgba(var(--civic-navy-deeper-rgb),0.06)', border: 'rgba(var(--civic-navy-deeper-rgb),0.10)' };

  return (
    <div className="space-y-6">
      {/* ── Consumer tiles ── */}
      <div className="grid md:grid-cols-3 gap-6">
        <SummaryTile
          tone="na"
          isDark={isDark}
          label={t('hospital:mappingsTable.summary.totalProcedures')}
          value={initialLoading ? '…' : (totalListed || 0).toLocaleString()}
          sub={listedSub}
          tooltip={t('hospital:mappingsTable.summary.listedTip')}
        />
        <SummaryTile
          tone={cashTone}
          isDark={isDark}
          label={t('hospital:mappingsTable.summary.cashLabel')}
          value={cashValue}
          pill={cashPill}
          sub={cashSub}
          tooltip={t('hospital:mappingsTable.summary.cashTip')}
        />
        <SummaryTile
          tone={aboveTone}
          isDark={isDark}
          label={t('hospital:mappingsTable.summary.aboveLabel')}
          value={aboveValue}
          pill={abovePill}
          sub={aboveSub}
          tooltip={t('hospital:mappingsTable.summary.aboveTip')}
        />
      </div>

      {/* ── What common care costs here ── */}
      {rows.length > 0 && (
        <Card className="terminal-card p-5 md:p-6">
          <div className="flex flex-col gap-1 mb-4">
            <h3 className="text-lg font-semibold">{t('hospital:mappingsTable.commonCare.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('hospital:mappingsTable.commonCare.subtitle')}</p>
          </div>

          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-xs md:text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.10em] font-bold text-muted-foreground" style={{ background: palette.headBg }}>
                  <th className="text-left px-2 md:px-3 py-2 rounded-l-md">{t('hospital:mappingsTable.commonCare.colService')}</th>
                  <th className="text-right px-2 md:px-3 py-2 whitespace-nowrap">{t('hospital:mappingsTable.commonCare.colList')}</th>
                  {anyCash && <th className="text-right px-2 md:px-3 py-2 whitespace-nowrap">{t('hospital:mappingsTable.commonCare.colCash')}</th>}
                  {anyStats && <th className="hidden md:table-cell text-right px-2 md:px-3 py-2 whitespace-nowrap">{t('hospital:mappingsTable.commonCare.colMedian')}</th>}
                  {anyStats && (
                    <th className="text-right px-2 md:px-3 py-2 whitespace-nowrap rounded-r-md">
                      <span className="md:hidden">{t('hospital:mappingsTable.commonCare.colVsShort')}</span>
                      <span className="hidden md:inline">{t('hospital:mappingsTable.commonCare.colVs')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ group, rows: groupRows }) => (
                  <GroupRows
                    key={group}
                    label={t(`hospital:mappingsTable.commonCare.groups.${group}`)}
                    rows={groupRows}
                    anyCash={anyCash}
                    anyStats={anyStats}
                    palette={palette}
                    onPickCode={onPickCode}
                    nameOf={(def) => t(`hospital:mappingsTable.commonCare.names.${def.family}_${def.code}`, {
                      defaultValue: (def.family === 'cpt' ? cptStats : drgStats)?.data?.[def.code]?.n || `${def.family.toUpperCase()} ${def.code}`,
                    })}
                    positionLabel={(p) => t(`hospital:mappingsTable.commonCare.position.${p}`)}
                    positionShortLabel={(p) => t(`hospital:mappingsTable.commonCare.positionShort.${p}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {(hiddenCount > 0 || showAll) && (
            <button
              type="button"
              onClick={() => setShowAll(v => !v)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {showAll
                ? <><ChevronUp className="w-4 h-4" /> {t('hospital:mappingsTable.commonCare.showLess')}</>
                : <><ChevronDown className="w-4 h-4" /> {t('hospital:mappingsTable.commonCare.showAll', { n: rows.length })}</>}
            </button>
          )}

          <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
            {anyStats
              ? t('hospital:mappingsTable.commonCare.footnote', { hospitals: (statsHospitals ?? 0).toLocaleString() })
              : statsLoading
                ? t('hospital:mappingsTable.summary.abovePending')
                : t('hospital:mappingsTable.commonCare.noStats')}
          </p>
          {/* Our names, not the AMA's descriptors — say so on the page, with the
              CPT® trademark line (Trent, 2026-09-16). */}
          <p className="mt-2 text-[11px] text-muted-foreground/80 leading-relaxed">
            {t('hospital:mappingsTable.commonCare.cptNote')}
          </p>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function GroupRows({ label, rows, anyCash, anyStats, palette, onPickCode, nameOf, positionLabel, positionShortLabel }: {
  label: string;
  rows: CommonRow[];
  anyCash: boolean;
  anyStats: boolean;
  palette: Record<string, string>;
  onPickCode: (code: string, family: CommonCareFamily) => void;
  nameOf: (def: CommonCareCode) => string;
  positionLabel: (p: Position) => string;
  positionShortLabel: (p: Position) => string;
}) {
  const colSpan = 2 + (anyCash ? 1 : 0) + (anyStats ? 2 : 0);
  return (
    <>
      <tr>
        <td colSpan={colSpan} className="px-2 md:px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">
          {label}
        </td>
      </tr>
      {rows.map(r => {
        const tone = r.position ? POSITION_TONE[r.position] : 'na';
        const color = palette[tone];
        return (
          <tr
            key={`${r.def.family}_${r.def.code}`}
            onClick={() => onPickCode(r.def.code, r.def.family)}
            className="cursor-pointer transition-colors"
            style={{ borderTop: `1px solid ${palette.border}` }}
            onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = palette.rowHover; }}
            onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
            title={`${r.def.family === 'drg' ? 'MS-DRG' : 'CPT'} ${r.def.code}${r.desc ? ` — hospital's own wording: ${r.desc}` : ''}`}
          >
            <td className="px-2 md:px-3 py-2 align-middle">
              <div className="font-medium leading-snug">{nameOf(r.def)}</div>
              <span
                className="inline-block mt-0.5 text-[10px] font-mono tracking-wide px-1.5 py-[1px] rounded-sm text-muted-foreground whitespace-nowrap"
                style={{ background: palette.chipBg }}
              >
                {r.def.family === 'drg' ? 'MS-DRG' : 'CPT'} {r.def.code}
              </span>
            </td>
            <td className="px-2 md:px-3 py-2 text-right font-mono font-bold whitespace-nowrap align-middle">{fmtUsd(r.price)}</td>
            {anyCash && (
              <td className="px-2 md:px-3 py-2 text-right font-mono whitespace-nowrap align-middle" style={{ color: r.cash != null ? palette.good : undefined }}>
                {r.cash != null ? fmtUsd(r.cash) : <span className="text-muted-foreground">—</span>}
              </td>
            )}
            {anyStats && (
              <td className="hidden md:table-cell px-2 md:px-3 py-2 text-right font-mono whitespace-nowrap align-middle text-muted-foreground">
                {r.median != null ? fmtUsd(r.median) : '—'}
              </td>
            )}
            {anyStats && (
              <td className="px-2 md:px-3 py-2 text-right whitespace-nowrap align-middle">
                {r.position && r.diffPct != null ? (
                  <span className="inline-flex flex-col items-end gap-0.5 md:flex-row md:items-baseline md:gap-1.5">
                    <span className="font-mono font-bold" style={{ color }}>{fmtPct(r.diffPct)}</span>
                    <span
                      className="text-[9px] uppercase tracking-[0.08em] font-bold px-1.5 py-[2px] rounded-sm border"
                      style={{ color, borderColor: `${color}66`, background: `${color}14` }}
                    >
                      <span className="md:hidden">{positionShortLabel(r.position)}</span>
                      <span className="hidden md:inline">{positionLabel(r.position)}</span>
                    </span>
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SummaryTile({ tone, isDark, label, value, sub, pill, tooltip }: {
  tone: Tone;
  isDark: boolean;
  label: string;
  value: string;
  sub?: string;
  pill?: string;
  tooltip?: string;
}) {
  const stripe: Record<Tone, string> = isDark
    ? { good: '#10b981', neutral: 'var(--civic-gold)', bad: 'var(--civic-red)', na: '#7B8AA0' }
    : { good: '#0F8A5F', neutral: 'var(--civic-gold)', bad: 'var(--civic-red)', na: '#94A3B8' };
  const text: Record<Tone, string> = isDark
    ? { good: '#86efac', neutral: 'var(--civic-parchment)', bad: '#FCA5A5', na: '#D4C9AE' }
    : { good: '#0F6B49', neutral: 'var(--civic-gold-ink)', bad: '#8B1828', na: '#475569' };
  const c = stripe[tone];
  return (
    <Card className="terminal-card relative p-5 pl-6 h-full">
      <div
        className="absolute top-3 bottom-3 left-0 w-[3px] rounded-full"
        style={{ background: c, boxShadow: `0 0 8px ${c}55` }}
        aria-hidden
      />
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-[0.10em] font-bold text-muted-foreground inline-flex items-center gap-1">
          {label}
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="cursor-help inline-flex" aria-label={label}>
                  <Info className="w-3 h-3" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">{tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {pill && (
          <span
            className="text-[9px] uppercase tracking-[0.08em] font-bold px-2 py-[2px] rounded-sm border whitespace-nowrap"
            style={{ color: text[tone], borderColor: `${c}88`, background: `${c}1f` }}
          >
            {pill}
          </span>
        )}
      </div>
      <div className="text-2xl md:text-3xl font-mono font-bold leading-tight" style={{ color: tone === 'na' ? undefined : text[tone] }}>
        {value}
      </div>
      {sub && <div className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{sub}</div>}
    </Card>
  );
}

export default HospitalPriceSummary;
